// Ensure Credentials API exists before auth libraries initialize.
// Some browsers/environments expose no navigator.credentials,
// while some lock the property descriptor on navigator.

type CredentialsLike = {
  create: (...args: unknown[]) => Promise<never>;
  get: (...args: unknown[]) => Promise<never>;
  store: (...args: unknown[]) => Promise<never>;
  preventSilentAccess: (...args: unknown[]) => Promise<void>;
};

function buildNotSupportedError(): Error {
  if (typeof DOMException !== "undefined") {
    return new DOMException(
      "navigator.credentials is not supported in this browser",
      "NotSupportedError",
    );
  }
  return new Error("navigator.credentials is not supported in this browser");
}

function buildCredentialsShim(): CredentialsLike {
  return {
    create: async () => Promise.reject(buildNotSupportedError()),
    get: async () => Promise.reject(buildNotSupportedError()),
    store: async () => Promise.reject(buildNotSupportedError()),
    preventSilentAccess: async () => Promise.resolve(),
  };
}

function hasCreate(): boolean {
  try {
    return (
      typeof navigator !== "undefined" &&
      !!navigator.credentials &&
      typeof navigator.credentials.create === "function"
    );
  } catch {
    return false;
  }
}

// Module-level shim: runs before Clerk's static imports execute.
// Mirrors the inline HTML polyfill exactly so both paths are consistent.
(function installCredentialsShim(): void {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return;
  }

  // Already patched by the inline HTML polyfill or natively supported — nothing to do.
  if (hasCreate()) {
    return;
  }

  const shim = buildCredentialsShim();

  const targets = [
    navigator as object,
    Object.getPrototypeOf(navigator) as object,
    ((window as unknown as { Navigator?: { prototype?: object } }).Navigator
      ?.prototype) as object | undefined,
  ].filter(Boolean) as object[];

  for (const target of targets) {
    try {
      // Use a get accessor — NOT a value descriptor — so we can override existing
      // accessor descriptors on Navigator.prototype (e.g. Firefox private mode).
      Object.defineProperty(target, "credentials", {
        configurable: true,
        enumerable: false,
        get: () => shim,
      });
      if (hasCreate()) return;
    } catch {
      // This target is locked; try the next one.
    }
  }

  // Last resort: plain assignment.
  try {
    (navigator as unknown as { credentials?: CredentialsLike }).credentials = shim;
  } catch {
    // Truly unsupported environment; nothing more we can do.
  }
})();
